"use client";

import { useState } from "react";
import { MessageSquare, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateTicketUpdate } from "@/lib/hooks/api/tickets";

/**
 * Canonical update-type options for `ticket_updates.update_type`.
 * Shared by the Hub modal/quick-action form; the Slack add-ticket-update modal
 * mirrors this list (lib/integrations/slack/modals/features/sales/add-ticket-update.ts).
 */
export const TICKET_UPDATE_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "comment", label: "Comment" },
  { value: "status_change", label: "Status Change" },
  { value: "priority_change", label: "Priority Change" },
  { value: "assignment_change", label: "Assignment Change" },
  { value: "resolution", label: "Resolution" },
  { value: "customer_communication", label: "Customer Communication" },
  { value: "internal_note", label: "Internal Note" },
];

interface TicketUpdateFormProps {
  ticketId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * Form for adding a `ticket_updates` row: update type + content + internal flag.
 * Used by the global add-update modal (AddTicketUpdateModalContainer) and the
 * inline QuickTicketUpdateAction button.
 */
export function TicketUpdateForm({ ticketId, onSuccess, onCancel }: TicketUpdateFormProps) {
  const [updateType, setUpdateType] = useState("comment");
  const [content, setContent] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const createTicketUpdate = useCreateTicketUpdate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    try {
      await createTicketUpdate.mutateAsync({
        ticket_id: ticketId,
        update_type: updateType,
        content: content.trim(),
        is_internal: isInternal || updateType === "internal_note",
      });
      onSuccess();
    } catch {
      // Mutation hook surfaces its own error toast; keep the form open so the
      // user can retry without losing their text.
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="ticket-update-type" className="text-sm font-medium">
          Update Type
        </Label>
        <Select value={updateType} onValueChange={setUpdateType}>
          <SelectTrigger id="ticket-update-type" className="w-full">
            <SelectValue placeholder="Select update type" />
          </SelectTrigger>
          <SelectContent>
            {TICKET_UPDATE_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-update-content" className="text-sm font-medium">
          Update Content *
        </Label>
        <Textarea
          id="ticket-update-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What happened? Progress made, info gathered, next step…"
          rows={4}
          className="resize-y"
          required
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="ticket-update-internal"
          checked={isInternal}
          onCheckedChange={(checked) => setIsInternal(!!checked)}
        />
        <Label
          htmlFor="ticket-update-internal"
          className="flex items-center gap-2 cursor-pointer text-sm"
        >
          <Lock className="h-4 w-4 text-red-500" />
          Internal update (not visible to clients)
        </Label>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={createTicketUpdate.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={createTicketUpdate.isPending || !content.trim()}
          className="gap-2"
        >
          <MessageSquare className="h-4 w-4" />
          {createTicketUpdate.isPending ? "Adding…" : "Add Update"}
        </Button>
      </div>
    </form>
  );
}
